/**
 * Hardware-keyboard input for the PIN keypad (DVL-030), kept apart from the component so it can be tested
 * without a DOM.
 */

/** Keypads currently on screen, in mount order — only the last one (the topmost) takes keys. */
export const activeKeypads: symbol[] = [];

export type PinKey = '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | 'backspace';

interface KeyLike {
  key: string;
  code?: string;
  repeat?: boolean;
  ctrlKey?: boolean;
  metaKey?: boolean;
  altKey?: boolean;
  isComposing?: boolean;
  target?: EventTarget | null;
}

/** A text field has focus — the key belongs to it (an e-mail code next to the keypad, a search box). */
function typingIntoField(target: EventTarget | null | undefined): boolean {
  const el = target as { tagName?: string; isContentEditable?: boolean } | null | undefined;
  if (!el || typeof el.tagName !== 'string') return false;
  const tag = el.tagName.toUpperCase();
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable === true;
}

/**
 * The PIN key a keyboard event means, or null when the keypad must ignore it: held-down auto-repeat (one press is
 * one digit), shortcuts with Ctrl/⌘/Alt, IME composition, and anything typed into a text field.
 * Numpad digits are read from {@code code} as well, so they work with NumLock off.
 */
export function pinKeyFromEvent(e: KeyLike): PinKey | null {
  if (e.repeat || e.ctrlKey || e.metaKey || e.altKey || e.isComposing) return null;
  if (typingIntoField(e.target)) return null;
  if (/^[0-9]$/.test(e.key)) return e.key as PinKey;
  const numpad = /^Numpad([0-9])$/.exec(e.code ?? '');
  if (numpad) return numpad[1] as PinKey;
  if (e.key === 'Backspace' || e.key === 'Delete') return 'backspace';
  return null;
}
